import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney, formatNumber, formatRelative } from '../ui/format';
import { Badge } from '../ui/Page';
import {
  getLeadWorkspace,
  listLeadMoments,
  updateRecord,
  whatsappNumber,
  type RecordDetail,
} from './detail-api';

/**
 * What somebody needs to see about a lead without scrolling: how much we know, how much effort has
 * gone in, what the next step is, and what it is worth.
 *
 * The next step being absent is the most useful thing here. A lead nobody has a next step for is a
 * lead about to be forgotten, and the point of putting it at the top is that somebody fixes it in the
 * same visit rather than discovering it in a report next quarter.
 */
export function LeadSidebar({
  leadId,
  lead,
}: {
  leadId: string;
  lead: RecordDetail;
}): React.JSX.Element {
  const workspace = useQuery({
    queryKey: ['lead-workspace', leadId],
    queryFn: () => getLeadWorkspace(leadId),
  });

  const data = workspace.data;
  const phone = whatsappNumber(lead.phone as string | null);

  return (
    <div className="lead-side">
      <section className="lead-side__block">
        <span className="lead-side__label">{t('lead.knownScore')}</span>

        <div className="cell-inline">
          <span className="metric__value">{data ? `${data.completeness.score}%` : '—'}</span>

          <span className="funnel__track lead-side__bar">
            <span className="funnel__fill" style={{ width: `${data?.completeness.score ?? 0}%` }} />
          </span>
        </div>

        {data && data.completeness.missing.length > 0 && (
          <p className="muted small">
            {t('lead.missing')}:{' '}
            {data.completeness.missing
              .map((field) => t(`lead.field.${field.key}`) || field.label)
              .join(', ')}
          </p>
        )}
      </section>

      {phone && (
        <a
          className="button button--whatsapp"
          href={`https://wa.me/${phone}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('lead.openWhatsapp')}
        </a>
      )}

      <section className="lead-side__block">
        <span className="lead-side__label">{t('lead.nextStep')}</span>

        {data?.nextStep ? (
          <Link to={`/trabalho/tarefas/${data.nextStep.taskId}`} className="cell-stack">
            <span className="strong">{data.nextStep.title}</span>
            {data.nextStep.dueDate && (
              <span className="muted small">{formatDate(data.nextStep.dueDate)}</span>
            )}
          </Link>
        ) : (
          /* The state worth seeing: nobody said what happens next. */
          <p className="lead-idle">{t('lead.noNextStep')}</p>
        )}
      </section>

      <section className="lead-side__block">
        <div className="lead-side__counts">
          <span className="cell-stack">
            <span className="metric__value">{formatNumber(data?.interactions ?? 0)}</span>
            <span className="muted small">{t('lead.interactions')}</span>
          </span>

          <span className="cell-stack">
            <span className="metric__value">{formatNumber(data?.unansweredCalls ?? 0)}</span>
            <span className="muted small">{t('lead.unanswered')}</span>
          </span>
        </div>

        {data && data.unansweredCalls >= 3 && (
          /* Four unanswered calls is not a lead that needs a fifth. */
          <p className="muted small">{t('lead.tryAnotherChannel')}</p>
        )}

        {data?.lastInteractionAt && (
          <p className="muted small">
            {t('lead.lastContact')}: {formatRelative(data.lastInteractionAt)}
          </p>
        )}
      </section>

      <DealValue leadId={leadId} lead={lead} />
      <Probability leadId={leadId} lead={lead} />
      <Moment leadId={leadId} lead={lead} />
    </div>
  );
}

/** What this person can spend. Typed by the person who asked them, because nothing else knows. */
function DealValue({ leadId, lead }: { leadId: string; lead: RecordDetail }): React.JSX.Element {
  const client = useQueryClient();
  const current = Number(lead.deal_value_cents ?? 0);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(current / 100));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      updateRecord('leads', leadId, {
        dealValueCents: Math.round(Number(value.replace(',', '.')) * 100),
      }),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void client.invalidateQueries({ queryKey: ['record', 'leads', leadId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <section className="lead-side__block">
      <span className="lead-side__label">{t('lead.dealValue')}</span>

      {editing ? (
        <form
          className="cell-inline"
          onSubmit={(event) => {
            event.preventDefault();
            if (!Number.isNaN(Number(value.replace(',', '.')))) save.mutate();
          }}
        >
          <input
            value={value}
            inputMode="decimal"
            autoFocus
            onChange={(event) => setValue(event.target.value)}
          />
          <button type="submit" className="button button--primary button--compact">
            {t('common.save')}
          </button>
        </form>
      ) : (
        <button type="button" className="lead-side__value" onClick={() => setEditing(true)}>
          {formatMoney(current)}
        </button>
      )}

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * One to five, the rep's own read.
 *
 * Deliberately not derived from anything. It is the judgement of the person who actually spoke to
 * them, which is information no score has — and a field that a model would overwrite is a field
 * people stop filling in.
 */
function Probability({ leadId, lead }: { leadId: string; lead: RecordDetail }): React.JSX.Element {
  const client = useQueryClient();
  const current = lead.sale_probability as number | null;

  const set = useMutation({
    mutationFn: (value: number) => updateRecord('leads', leadId, { saleProbability: value }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['record', 'leads', leadId] }),
  });

  return (
    <section className="lead-side__block">
      <span className="lead-side__label">{t('lead.probability')}</span>
      <span className="muted small">{t('lead.probabilityHint')}</span>

      <div className="scale" role="group" aria-label={t('lead.probability')}>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            className={`scale__step${current !== null && value <= current ? ' is-on' : ''}`}
            disabled={set.isPending}
            onClick={() => set.mutate(value)}
          >
            {value}
          </button>
        ))}
      </div>
    </section>
  );
}

/** Where they are in their own decision, which is not where they are in our funnel. */
function Moment({ leadId, lead }: { leadId: string; lead: RecordDetail }): React.JSX.Element {
  const client = useQueryClient();

  const moments = useQuery({
    queryKey: ['lead-moments'],
    queryFn: listLeadMoments,
    staleTime: 5 * 60 * 1000,
  });

  const set = useMutation({
    mutationFn: (momentId: string) => updateRecord('leads', leadId, { momentId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['record', 'leads', leadId] }),
  });

  const current = lead.moment_id as string | null;
  const chosen = (moments.data?.moments ?? []).find((moment) => moment.id === current);

  return (
    <section className="lead-side__block">
      <span className="lead-side__label">{t('lead.moment')}</span>

      {chosen && (
        <Badge tone="info">
          <span
            className="lead-side__dot"
            style={{ background: chosen.color ?? 'currentColor' }}
            aria-hidden="true"
          />
          {chosen.name}
        </Badge>
      )}

      <select
        className="select"
        value={current ?? ''}
        disabled={set.isPending}
        onChange={(event) => {
          if (event.target.value) set.mutate(event.target.value);
        }}
      >
        <option value="">{t('lead.chooseMoment')}</option>
        {(moments.data?.moments ?? []).map((moment) => (
          <option key={moment.id} value={moment.id}>
            {moment.name}
          </option>
        ))}
      </select>
    </section>
  );
}
